import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
    Button,
    Form,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { onChangeFunction, UrlEnum } from '../../../shared';
import { FormSelect } from '../../blocks';
import { getCipLabel } from '../../academic-affair-report/program-criteria-form';
import { getOptionLabel } from '../../academic-affair-program/search-academic-programs-form';
import { useHistory } from 'react-router-dom';
import format from 'date-fns/format';
import { academicProgramsService, capitalProjectService } from '../../../services';
import queryString from 'query-string';
import { RadioCheckGroup } from '../../academic-affair-report/components/RadioCheckGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faFile,
    faPaperclip,
    faTimes,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import eventEmitter from '../../../shared/eventEmitter';

export const ProgramRequestEditProgramRequestModal = ({
    isOpen,
    toggle,
    institutions,
    details,
    degreeLevels,
    INITIAL_STATE,
    setDetails,
}) => {
    const locationStatusOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];
    const history = useHistory();
    const [onlineDropdownDisabled, setOnlineDropdownDisabled] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [locationsToAdd, setLocationsToAdd] = useState([]);
    const [allFieldsFilled, setAllFieldsFilled] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const reducer = (state, newState) => ({ ...state, ...newState });
    const [allPrograms, setAllPrograms] = useState([]);

    const [userInput, setUserInput] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        INITIAL_STATE
    );
    const [files, setFiles] = useState(null);

    useEffect(() => {
        setUserInput(INITIAL_STATE);
    }, [INITIAL_STATE]);

    const toggleModal = () => {
        toggle();
        setOnlineDropdownDisabled(false);
        setFiles(null);
        setUserInput(INITIAL_STATE);
        setSubmitted(false);
    };
    const [splitPrograms, setSplitPrograms] = useState([]);
    const [currentSplitIndex, setCurrentSplitIndex] = useState(0);

    const [options, setOptions] = useReducer(reducer, {});
    const fetchOptions = useCallback(() => {
        academicProgramsService.getInstitutions().then(res => {
            setOptions({
                institutions: res?.map(x => {
                    return { value: x.label, label: x.value };
                }),
            });
        });
        academicProgramsService.getProgramStatusTypes().then(res => {
            setOptions({
                programStatusOptions: res?.map(x => ({
                    value: x.id,
                    label: x.name,
                })),
            });
        });
        academicProgramsService.getCips().then(res => {
            setOptions({
                cipCode: res[2],
            });
        });
        academicProgramsService.getDegreeLevels().then(res => {
            setOptions({
                degreeLevels: res?.map(x => {
                    return { value: x.label, label: x.value };
                }),
            });
        });
    }, []);

    const onFileChange = useCallback(e => {
        setFiles(e.target.files);
    }, []);

    useEffect(() => {
        setOptions({ locations: [] });
        if (institutions.label !== undefined) {
            const query = queryString.stringify(
                { institutions: institutions.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res?.locations });
            });
        } else if (userInput.institution) {
            const query = queryString.stringify(
                { institutions: userInput.institution.value },
                {
                    arrayFormat: 'separator',
                    skipEmptyString: true,
                }
            );
            academicProgramsService.getLocations(query).then(res => {
                setOptions({ locations: res.locations });
            });
        }
    }, [userInput.institution, institutions]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    useEffect(() => {
        const institutionId =
            userInput.institution?.value ??
            userInput.institution?.[0]?.value ??
            institutions?.value;

        if (!institutionId) return;

        setAllPrograms([]);
        setOptions({ existingPrograms: [] });
        setUserInput({ editProgram: [] });

        const query = queryString.stringify(
            { institutions: institutionId },
            {
                arrayFormat: 'separator',
                skipEmptyString: true,
            }
        );
        academicProgramsService.getAllProgramDetailsFromInstitution(query).then(res => {
            const programs = res?.programs?.flat() ?? [];
            const formattedPrograms = programs.map(program => ({
                value: program.programId,
                label: `${program.programId} - ${program.programFormalName}`,
            }));

            setAllPrograms(programs);
            setOptions({ existingPrograms: formattedPrograms });
            // ✅ NEW: map comma-separated IDs to selected options
            if (details?.consolidatedProgramIds) {
                const ids = details.consolidatedProgramIds
                    .split(',')
                    .map(x => Number(x.trim()));
                const selectedPrograms = formattedPrograms.filter(option =>
                    ids.includes(option.value)
                );
                setUserInput({ editProgram: selectedPrograms });
            }
        });
    }, [userInput.institution, institutions, details]);

    const getEditExistingValidationError = () => {
        const t = userInput.requestType;
        if (t === '1') {
            if (!userInput.programName) return 'Program Name is required.';
            if (!userInput.cipCode) return 'CIP Code is required.';
            if (!userInput.programDistanceEd && userInput.programDistanceEd !== 0) return 'Delivery Mode is required.';
            if (!userInput.programDistanceEdOnline && userInput.programDistanceEdOnline !== 0) return 'Nature of Online Program is required.';
            if (!userInput.programStatus) return 'Program Status is required.';
        }
        if (t === '10') {
            if (!userInput.programName) return 'Program Name is required.';
            if (!userInput.cipCode) return 'CIP Code is required.';
            if (!userInput.degreeLevel) return 'Degree/Award Level is required.';
            if (!userInput.programDistanceEd && userInput.programDistanceEd !== 0) return 'Delivery Mode is required.';
            if (!userInput.programDistanceEdOnline && userInput.programDistanceEdOnline !== 0) return 'Nature of Online Program is required.';
            if (!userInput.locations || userInput.locations.length === 0) return 'At least one location is required.';
        }
        if (t === '9') {
            const hasNewLocations = locationsToAdd.length > 0;
            const hasStatusChange = (userInput.locations || []).some(
                loc => loc.statusId !== loc.originalStatusId
            );
            if (!hasNewLocations && !hasStatusChange)
                return 'At least one location must be added or have its status changed.';
        }

        if (t === '4' && !userInput.cipCode) return 'CIP Code is required.';
        if (t === '2' && !userInput.programName) return 'Program Name is required.';
        if (t === '11' && !userInput.degreeLevel) return 'Degree/Award Level is required.';
        if (t === '5' && !userInput.programDistanceEd && userInput.programDistanceEd !== 0) return 'Delivery Mode is required.';
        if (t === '5' && !userInput.programDistanceEdOnline && userInput.programDistanceEdOnline !== 0) return 'Nature of Online Program is required.';
        if (t === '6' && !userInput.programStatus) return 'Program Status is required.';
        return '';
    };

    const editProgramRequest = async () => {
        const validationError = getEditExistingValidationError();
        if (validationError) {
            setSubmitted(true);
            return;
        }
        try {
            setSubmitting(true);
            const postData = {
                requestId: details.requestID,
                consolidatedProgramIds: userInput.editProgram
                    ?.map(x => x.value)
                    .join(','),
                degreeLevel: userInput.degreeLevel
                    ? userInput.degreeLevel.value
                    : undefined,
                formalName: userInput.programName ? userInput.programName : undefined,
                cipCode: userInput.cipCode ? userInput.cipCode.value : undefined,
                distanceEdApproved: {
                    id: userInput.programDistanceEd,
                },
                distanceEdOnline: {
                    id: userInput.programDistanceEdOnline,
                },
                locationStatusChanges: getLocationStatusChanges([
                    ...locationsToAdd,
                    ...userInput.locations,
                ]),
                comments: userInput.comments ? userInput.comments : undefined,
                creditRequirementMinimum: userInput.creditRequirementMinimum
                    ? Number(userInput.creditRequirementMinimum)
                    : undefined,
                creditRequirementMaximum: userInput.creditRequirementMaximum
                    ? Number(userInput.creditRequirementMaximum)
                    : undefined,
                institutionId: institutions ? institutions.value : undefined,
                credential: {
                    ProgramURL: userInput.programURL ? userInput.programURL : '',
                    Keywords: userInput.keywords ? userInput.keywords : '',
                    ProgramDescription: userInput.description
                        ? userInput.description
                        : '',
                    CredentialName: userInput.programName
                        ? userInput.programName
                        : undefined,
                },
                ProgramStatusId: userInput.programStatus
                    ? userInput.programStatus.value
                    : undefined,
            };
            await academicProgramsService.editProgramRequest(postData).then(postRes => {
                if (files) {
                    Promise.resolve(
                        academicProgramsService.uploadFiles(files, details.requestID)
                    );
                }
                eventEmitter.emit('updateSARData');
                return postRes;
            });
            setSubmitting(false);
            toggle();
            setUserInput(INITIAL_STATE);
        } catch (error) {
            setSubmitting(false);
        }
    };

    const updateLocations = evt => {
        const newLocation = {
            label: evt.locations[0].label,
            value: evt.locations[0].value,
            statusId: 1,
        };
        setLocationsToAdd(prev => [...prev, newLocation]);
    };

    const getLocationStatusChanges = locations => {
        return locations.map(location => {
            return {
                LocationId: location.value,
                LocationName: location.label,
                StatusId: location.statusId,
            };
        });
    };

    useEffect(() => {
        const skipKeys = [
            'endDate',
            'comments',
            'creditRequirementMinimum',
            'creditRequirementMaximum',
            'cipCodeTitle',
            'programId',
        ];
        var anyEmpty = false;
        for (const key in userInput) {
            if (skipKeys.includes(key)) continue;
            if (userInput[key] === null || userInput[key] === '') {
                anyEmpty = true;
            }
        }
        setAllFieldsFilled(!anyEmpty);
    }, [userInput]);

    const closeBtn = (
        <button className="close" onClick={toggleModal}>
            &times;
        </button>
    );

    const rowStyles = {
        container: provided => ({
            ...provided,
            width: 120,
        }),
    };

    const statusChangeOptions = [
        { label: 'Active', value: 1 },
        { label: 'Suspended', value: 3 },
        { label: 'Eliminated', value: 4 },
    ];

    const getSelectOption = val => {
        return statusChangeOptions.find(x => x.value === val);
    };

    const renderApprovedLocationRows = () => {
        let rows = [];
        locationsToAdd.map(location => {
            rows.push(
                <tr key={location.value}>
                    <td>{location.label}</td>
                    <td className="table-actions">
                        <div className="text-success">Pending Addition</div>
                        <div className="text-danger">
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="cursor-pointer ml-2"
                                onClick={() =>
                                    setLocationsToAdd(prev =>
                                        prev.filter(loc => loc.value !== location.value)
                                    )
                                }
                            />
                        </div>
                    </td>
                </tr>
            );
        });
        userInput.locations.map(location => {
            rows.push(
                <tr key={location.value}>
                    <td>{location.label}</td>
                    <td className="table-actions">
                        <Select
                            styles={rowStyles}
                            onChange={evt => {
                                const updatedLocations = userInput.locations.map(loc => {
                                    if (loc.value === location.value) {
                                        return { ...loc, statusId: evt.value };
                                    }
                                    return loc;
                                });
                                setUserInput({ locations: updatedLocations });
                            }}
                            options={statusChangeOptions}
                            value={getSelectOption(location.statusId)}
                            components={{
                                IndicatorSeparator: () => null,
                            }}
                        />
                    </td>
                </tr>
            );
        });
        return rows;
    };

    return (
        <Modal isOpen={isOpen} className="modal-dialog modal-lg">
            <ModalHeader close={closeBtn}>Edit Program Request</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <label>Institution</label>
                        <Input
                            type="text"
                            placeholder="Institution Name"
                            name="cipCode"
                            value={institutions.label}
                            disabled={true}
                        />
                    </FormGroup>
                      {userInput.requestType === '8' && (
                        <>
                            <FormGroup>
                                <label>Merged Program Name</label>
                                <Input
                                    type="text"
                                    placeholder="Program Name"
                                    onChange={evt =>
                                        onChangeFunction.handleChange(evt, setUserInput)
                                    }
                                    name="programName"
                                    value={userInput.programName}
                                />
                                {submitted && !userInput.programName && (
                                    <p className="text-danger mt-1">Program Name is required.</p>
                                )}
                            </FormGroup>
                        </>
                    )}
                    {userInput.requestType === '10' && (
                        <>
                            <FormSelect
                                label="CIP Code"
                                onChange={setUserInput}
                                options={
                                    options.cipCode
                                        ? options.cipCode.map(x => {
                                            return {
                                                value: x.cipCode,
                                                label: `${x.cipCode} - ${x.cipName}`,
                                            };
                                        })
                                        : []
                                }
                                placeholder="CIP Code"
                                name="cipCode"
                                value={userInput.cipCode}
                                isMulti={false}
                                getOptionLabel={getCipLabel}
                                isClearable
                                showLoading
                                required
                            />
                            {submitted && userInput.requestType === '10' && !userInput.cipCode && (
                                <p className="text-danger mt-1">CIP Code is required.</p>
                            )}
                            <div className="col-lg-6">
                                <label className="d-block">
                                    Delivery Mode<sup className="text-danger"> *</sup>
                                </label>
                                <RadioCheckGroup
                                    onChange={setUserInput}
                                    name="programDistanceEd"
                                    value={userInput.programDistanceEd}
                                    options={[
                                        {
                                            value: 1,
                                            label: 'On Campus Only',
                                        },
                                        {
                                            value: 2,
                                            label: 'Online Only',
                                        },
                                        {
                                            value: 3,
                                            label: 'On Campus & Online',
                                        },
                                    ]}
                                />
                            </div>
                            {submitted && userInput.requestType === '10' && !userInput.programDistanceEd && userInput.programDistanceEd !== 0 && (
                                <p className="text-danger mt-1">Delivery Mode is required.</p>
                            )}
                            {
                                <div className="col-lg-6">
                                    <label className="d-block">
                                        Nature of Online Program
                                        <sup className="text-danger"> *</sup>
                                    </label>
                                    <RadioCheckGroup
                                        onChange={setUserInput}
                                        name="programDistanceEdOnline"
                                        value={userInput.programDistanceEdOnline}
                                        options={[
                                            {
                                                value: 0,
                                                label: 'None',
                                            },
                                            {
                                                value: 1,
                                                label: '100% Online',
                                            },
                                            {
                                                value: 2,
                                                label: 'Blended',
                                            },
                                        ]}
                                    />
                                </div>
                            }
                            {submitted && userInput.requestType === '10' && !userInput.programDistanceEdOnline && userInput.programDistanceEdOnline !== 0 && (
                                <p className="text-danger mt-1">Nature of Online Program is required.</p>
                            )}
                            <div className="col-lg">
                                <FormGroup>
                                    <label>Credit Requirement Minimum</label>
                                    <Input
                                        type="number"
                                        style={{ textAlign: 'left' }}
                                        placeholder="Minimum Credits"
                                        onChange={evt =>
                                            onChangeFunction.handleChange(
                                                evt,
                                                setUserInput
                                            )
                                        }
                                        name="creditRequirementMinimum"
                                        value={userInput.creditRequirementMinimum}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-lg">
                                <FormGroup>
                                    <label>Credit Requirement Maximum</label>
                                    <Input
                                        type="number"
                                        style={{ textAlign: 'left' }}
                                        placeholder="Maximum Credits"
                                        onChange={evt =>
                                            onChangeFunction.handleChange(
                                                evt,
                                                setUserInput
                                            )
                                        }
                                        name="creditRequirementMaximum"
                                        value={userInput.creditRequirementMaximum}
                                    />
                                </FormGroup>
                            </div>
                        </>
                    )}
                    {userInput.requestType === '10' && (
                        <FormSelect
                            label="Select Locations"
                            onChange={setUserInput}
                            options={
                                options.locations
                                    ? options.locations.map(x => {
                                        return { value: x.key, label: x.value };
                                    })
                                    : []
                            }
                            closeAfterSelection={false}
                            placeholder="Select Locations"
                            name="locations"
                            value={userInput.locations}
                            isMulti
                            isClearable
                            showLoading
                            required
                        />
                    )}
                    {submitted && userInput.requestType === '10' && (!userInput.locations || userInput.locations.length === 0) && (
                        <p className="text-danger mt-1">At least one location is required.</p>
                    )}

                    {userInput.requestType !== '8' && userInput.requestType !== '10' && (
                        <FormGroup>
                            <label>Program Name</label>
                            <Input
                                type="text"
                                placeholder="Program Name"
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="programName"
                                disabled={
                                    userInput.requestType !== '2' &&
                                    userInput.requestType !== '1'
                                }
                                value={userInput.programName}
                            />
                            {submitted && (userInput.requestType === '1' || userInput.requestType === '2') && !userInput.programName && (
                                <p className="text-danger mt-1">Program Name is required.</p>
                            )}
                        </FormGroup>
                    )}
                    <FormGroup>
                        {(userInput.requestType === '4' ||
                            userInput.requestType === '1') && (
                                <FormSelect
                                    label="CIP Code"
                                    onChange={setUserInput}
                                    options={
                                        options.cipCode
                                            ? options.cipCode.map(x => {
                                                return {
                                                    value: x.cipCode,
                                                    label: `${x.cipCode} - ${x.cipName}`,
                                                };
                                            })
                                            : []
                                    }
                                    placeholder="CIP Code"
                                    name="cipCode"
                                    value={userInput.cipCode}
                                    isMulti={false}
                                    getOptionLabel={getCipLabel}
                                    isClearable
                                    showLoading
                                    required
                                />
                            )}
                        {submitted && (userInput.requestType === '4' || userInput.requestType === '1') && !userInput.cipCode && (
                            <p className="text-danger mt-1">CIP Code is required.</p>
                        )}
                    </FormGroup>

                    {(userInput.requestType === '9' || userInput.requestType === '1') && (
                        <div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Location</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>{renderApprovedLocationRows()}</tbody>
                            </table>
                        </div>
                    )}

                    {(userInput.requestType === '9' || userInput.requestType === '1') && (
                        <FormSelect
                            label="Select Locations To Add"
                            onChange={evt => updateLocations(evt)}
                            options={
                                options.locations
                                    ? options.locations
                                        .filter(loc => {
                                            const alreadySelected = userInput.locations.some(
                                                selected => selected.value === loc.key
                                            );
                                            const alreadyAdded = locationsToAdd.some(
                                                added => added.value === loc.key
                                            );
                                            return !alreadySelected && !alreadyAdded;
                                        })
                                        .map(x => {
                                            return { value: x.key, label: x.value };
                                        })
                                    : []
                            }
                            closeAfterSelection={true}
                            placeholder="Select a location to add to the program (if any)"
                            name="locations"
                            value={[]}
                            isMulti
                            isClearable
                            required
                        />
                    )}
                    {submitted && userInput.requestType === '9' && (() => {
                        const hasNewLocations = locationsToAdd.length > 0;
                        const hasStatusChange = (userInput.locations || []).some(
                            loc => loc.statusId !== loc.originalStatusId
                        );
                        return !hasNewLocations && !hasStatusChange ? (
                            <p className="text-danger mt-1">
                                At least one location must be added or have its status changed.
                            </p>
                        ) : null;
                    })()}
                    <div className="form-row align-items-center">
                        {(userInput.requestType === '5' ||
                            userInput.requestType === '1') && (
                                <>
                                    <div className="col-lg-6">
                                        <label className="d-block">
                                            Delivery Mode<sup className="text-danger"> *</sup>
                                        </label>
                                        <RadioCheckGroup
                                            onChange={setUserInput}
                                            name="programDistanceEd"
                                            value={userInput.programDistanceEd}
                                            options={[
                                                {
                                                    value: 1,
                                                    label: 'On Campus Only',
                                                },
                                                {
                                                    value: 2,
                                                    label: 'Online Only',
                                                },
                                                {
                                                    value: 3,
                                                    label: 'On Campus & Online',
                                                },
                                            ]}
                                        />
                                    </div>
                                    {submitted && (userInput.requestType === '5' || userInput.requestType === '1') && !userInput.programDistanceEd && userInput.programDistanceEd !== 0 && (
                                        <p className="text-danger mt-1">Delivery Mode is required.</p>
                                    )}
                                    {
                                        <div className="col-lg-6">
                                            <label className="d-block">
                                                Nature of Online Program
                                                <sup className="text-danger"> *</sup>
                                            </label>
                                            <RadioCheckGroup
                                                onChange={setUserInput}
                                                name="programDistanceEdOnline"
                                                value={userInput.programDistanceEdOnline}
                                                options={[
                                                    {
                                                        value: 0,
                                                        label: 'None',
                                                    },
                                                    {
                                                        value: 1,
                                                        label: '100% Online',
                                                    },
                                                    {
                                                        value: 2,
                                                        label: 'Blended',
                                                    },
                                                ]}
                                            />
                                        </div>
                                    }
                                    {submitted && (userInput.requestType === '5' || userInput.requestType === '1') && !userInput.programDistanceEdOnline && userInput.programDistanceEdOnline !== 0 && (
                                        <p className="text-danger mt-1">Nature of Online Program is required.</p>
                                    )}
                                </>
                            )}

                        {(userInput.requestType === '6' ||
                            userInput.requestType === '1') && (
                                <div className="col-lg-6">
                                    <FormSelect
                                        label="Program Status"
                                        onChange={setUserInput}
                                        options={options.programStatusOptions}
                                        placeholder="Status"
                                        name="programStatus"
                                        value={userInput.programStatus}
                                        isMulti={false}
                                        isClearable
                                        showLoading
                                        required
                                    />
                                    {submitted && (userInput.requestType === '6' || userInput.requestType === '1') && !userInput.programStatus && (
                                        <p className="text-danger mt-1">Program Status is required.</p>
                                    )}
                                </div>
                            )}

                        {(userInput.requestType === '11' ||
                            userInput.requestType === '10') && (
                                <div className="col-lg-6">
                                    <FormSelect
                                        label="Degree/Award Level"
                                        onChange={setUserInput}
                                        options={options.degreeLevels}
                                        placeholder="Degree/Award Level"
                                        name="degreeLevel"
                                        value={userInput.degreeLevel}
                                        isMulti={false}
                                        isClearable
                                        showLoading
                                        required
                                    />
                                    {submitted && (userInput.requestType === '11' || userInput.requestType === '10') && !userInput.degreeLevel && (
                                        <p className="text-danger mt-1">Degree/Award Level is required.</p>
                                    )}
                                </div>
                            )}

                           {(userInput.requestType === '3' ||
                            userInput.requestType === '1') && (
                                <>
                                    <div className="col-lg">
                                        <FormGroup>
                                            <label>Credit Requirement Minimum</label>
                                            <Input
                                                type="number"
                                                style={{ textAlign: 'left' }}
                                                placeholder="Minimum Credits"
                                                onChange={evt =>
                                                    onChangeFunction.handleChange(
                                                        evt,
                                                        setUserInput
                                                    )
                                                }
                                                name="creditRequirementMinimum"
                                                value={userInput.creditRequirementMinimum}
                                            />
                                        </FormGroup>
                                    </div>

                                    <div className="col-lg">
                                        <FormGroup>
                                            <label>Credit Requirement Maximum</label>
                                            <Input
                                                type="number"
                                                style={{ textAlign: 'left' }}
                                                placeholder="Maximum Credits"
                                                onChange={evt =>
                                                    onChangeFunction.handleChange(
                                                        evt,
                                                        setUserInput
                                                 )
                                                }
                                                name="creditRequirementMaximum"
                                                value={userInput.creditRequirementMaximum}
                                            />
                                      </FormGroup>
                                   </div>
                                </>
                            )}
                    </div>
                    <div className="form-row align-items-center">
                        <div className="col-lg-12">
                            <label>Support Documentation</label>
                        </div>
                    </div>
                    <div className="w-100 hidden-file-input-small">
                        <input
                            type="file"
                            accept=".csv,.zip,.pdf,.xls,.xlxs"
                            onChange={onFileChange}
                            multiple={true}
                        />
                        <button type="button" className="btn btn-outline-primary btn-sm">
                            <i className="far"></i> <FontAwesomeIcon icon={faUpload} />
                            Upload
                        </button>
                    </div>
                    {files &&
                        Array.from(files).map((x, index) => (
                            <h5 key={index} className="mb-0 mt-4 text-italic">
                                <FontAwesomeIcon icon={faPaperclip} /> {x.name}
                            </h5>
                        ))}
                    <FormGroup>
                        <label>
                            Please provide any additional information necessary to process
                            your request.
                        </label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="Place Comments Here"
                            onChange={evt =>
                                onChangeFunction.handleChange(evt, setUserInput)
                            }
                            name="comments"
                            value={userInput.comments}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>
                    Cancel
                </Button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => editProgramRequest()}
                    disabled={submitting}
                >
                    Submit
                </button>{' '}
            </ModalFooter>
        </Modal>
    );
};
